import { v4 as uuid } from 'uuid';

import { colorsPalettes } from '@similarweb/styles';

import { isIgnoredDomain } from '../domain/company';
import type { WidgetItemId } from '../domain/company_widgets';
import type { Contact, ContactStatusFilterValue, ContactDetailsFilterDto, PrivateData } from '../domain/contact';
import type { Country } from '../domain/country';
import type { EmailGenerationOptions, Reaction } from '../domain/generatedEmail';
import type { CollectedEmail, CollectedMeeting } from '../domain/meeting';
import type { Opportunity } from '../domain/opportunity';
import { CompanyTabName, Page } from '../domain/page';
import type { Pagination } from '../domain/pagination';
import type { Persona } from '../domain/persona';
import type { SettingsAppStorage } from '../domain/settings';
import type { Toast } from '../domain/toast';
import type { UserHasRequestedAccessSettings } from '../domain/user';

import { getExtensionModeState } from './store/utils';

export type ContactFilters = {
  searchText: string;
  persona?: Persona;
  includeCountries: Country[];
  contactDetails: ContactDetailsFilterDto;
  status: ContactStatusFilterValue;
};

export enum AppMode {
  Extension = 'extension',
  Salesforce = 'salesforce',
  ProSidebar = 'pro-sidebar',
  Standalone = 'standalone',
}

export type AiOutreachState = {
  isOpen: boolean;
  domain?: string;
  companyName?: string;
};

export type AppState = {
  isOpen: boolean;
  isEmailAgentOpen: boolean;
  emailAgentInstanceId?: string;
  aiOutreach: AiOutreachState;
  url?: string;
  companyLinkedinId?: string;
  toast?: Toast;
  page: Page;
  prevPage?: Page;
  contactLinkedinId?: string;
  contactLinkedinSalesNavId?: string;
  companyDomain?: string;
  collectedContactInfo?: Partial<Contact>;
  collectedContactsSearchInfo?: Partial<Contact>[];
  searchPagePagination: Pagination;
  contactFilters: ContactFilters;
  selectedContacts: Contact[];
  isMasterCheckboxChecked: boolean;
  companyTabName: CompanyTabName;
  notRevealedContactsIds: string[];
  settingsStorage: SettingsAppStorage;
  emailGenerationOptions: EmailGenerationOptions;
  collectedMeeting: CollectedMeeting;
  collectedEmail: CollectedEmail;
  collectedDomains: string[];
  isCompanyQuotaReached: boolean;
  mode: AppMode;
  hasRequestedAccess?: UserHasRequestedAccessSettings;
  trackingSectionName?: string;
  widgetId?: WidgetItemId;
};
export type Listener = () => void;
export type Unsubscribe = () => void;

export type AppStore = {
  subscribe: (listener: Listener) => Unsubscribe;
  getState: () => AppState;
  setUrl: (url: string) => void;
  setToast: (toast: Toast | undefined) => void;
  setErrorToast: (toast: Toast) => void;
  setSuccessToast: (toast: Omit<Toast, 'variant'>) => void;
  setCompanyLinkedinId: (companyLinkedinId?: string) => void;
  setCompanyDomain: (domain: string) => void;
  setCompanyPage: (params?: { companyDomain?: string; companyLinkedinId?: string }) => void;
  setContactPage: (params?: { linkedinId?: string; linkedinSalesNavId?: string }) => void;
  setSearchPage: () => void;
  setSettingsPage: () => void;
  resetSettingsPage: () => void;
  setCollectedContactInfo: (info: Partial<Contact>) => void;
  setCollectedContactsSearchInfo: (searchInfo: Partial<Contact>[]) => void;
  setContactsSearchText: (searchText: string) => void;
  setContactsPersona: (persona: Persona | undefined) => void;
  setContactsIncludeCountries: (includeCountries: Country[]) => void;
  setContactsDetails: (contactDetails: ContactDetailsFilterDto) => void;
  setContactsStatusFilterValue: (status: ContactStatusFilterValue) => void;
  setSelectedContacts: (selectedContacts: Contact[], isMasterCheckboxChecked?: boolean) => void;
  addToSelectedContacts: (contact: Contact) => void;
  removeFromSelectedContacts: (contact: Contact) => void;
  makeSelectedContactRevealed: (contact: Contact, privateData?: PrivateData) => void;
  setHomePage: () => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsEmailAgentOpen: (isEmailAgentOpen: boolean, instanceId?: string) => void;
  setAiOutreachOpen: (isOpen: boolean, domain?: string, companyName?: string) => void;
  setCompanyTabName: (companyTabName: CompanyTabName) => void;
  addNotRevealedContactId: (contactId: string) => void;
  getSettingsStorageValue: (key: keyof SettingsAppStorage) => SettingsAppStorage[keyof SettingsAppStorage] | undefined;
  setSettingsStorageValue: (keyValue: SettingsAppStorage) => void;
  setSearchPagePagination: (pagination: Pagination) => void;
  setCalendarPage: () => void;
  setEmailPage: () => void;
  setGoogleSheetsPage: () => void;
  incrementEmailsToGenerateCount: () => void;
  setCountByInsightId: (countByInsightId: Record<string, number>) => void;
  resetEmailGeneration: () => void;
  setRegeneratedOpportunitiesIds: (updatedOpportunitiesIds: string[]) => void;
  setUpdatedOpportunities: (updatedOpportunities: Opportunity[]) => void;
  setEmailReaction: (requestId: number, reaction: Reaction) => void;
  setShowToastAfterSuccessfulGeneration: (showToastAfterSuccessfulGeneration: boolean) => void;
  setIsFirstLoadingAfterOnboarding: (isFirstLoadingAfterOnboarding: boolean) => void;
  setEmailsToGenerateCount: (count: number) => void;
  setCollectedMeeting: (meeting: CollectedMeeting) => void;
  resetCollectedMeeting: () => void;
  setCollectedEmail: (email: CollectedEmail) => void;
  getCollectedMeeting: () => CollectedMeeting;
  setCompanyQuotaReached: (isCompanyQuotaReached: boolean) => void;
  setCollectedDomains: (domains: string[]) => void;
  setHasRequestedAccess: (value: Partial<UserHasRequestedAccessSettings>) => void;
  setWidgetId: (widgetId: WidgetItemId | undefined) => void;
};

export const INITIAL_EMAILS_TO_GENERATE = 3;

const modeState = getExtensionModeState();

export const INITIAL_CONTACTS_FILTER: ContactDetailsFilterDto = {
  hasEmail: false,
  hasDirectPhone: false,
  hasEmailAndMobilePhone: false,
  hasMobilePhone: false,
};

export const initialState: AppState = {
  contactFilters: { searchText: '', includeCountries: [], contactDetails: INITIAL_CONTACTS_FILTER, status: {} },
  selectedContacts: [],
  isMasterCheckboxChecked: false,
  companyTabName: CompanyTabName.Overview,
  notRevealedContactsIds: [],
  settingsStorage: {},
  searchPagePagination: { pageSize: 25, page: 1, total: 0 },
  emailGenerationOptions: {
    emailsToGenerate: INITIAL_EMAILS_TO_GENERATE,
    countByInsightId: {},
    sessionId: uuid(),
    emailReactions: {},
    regeneratedOpportunitiesIds: [],
    updatedOpportunities: [],
    showToastAfterSuccessfulGeneration: false,
    isFirstLoadingAfterOnboarding: false,
  },
  collectedMeeting: { title: '', time: '', guests: [], domains: [], isNew: false },
  collectedEmail: {},
  collectedDomains: [],
  isCompanyQuotaReached: false,
  isEmailAgentOpen: false,
  aiOutreach: { isOpen: false },
  hasRequestedAccess: {},
  ...modeState,
};

const makeAppStore = (): AppStore => {
  let listeners: Listener[] = [];
  let appState = initialState;
  const emitChange = () => listeners.forEach((l) => l());

  return {
    subscribe: (listener: Listener): Unsubscribe => {
      listeners = listeners.concat(listener);
      return () => {
        listeners = listeners.filter((l) => l !== listener);
      };
    },

    getState: () => appState,

    setUrl: (url: string) => {
      if (appState.url === url) {
        return;
      }
      appState = { ...appState, url };
      emitChange();
    },

    setIsOpen: (isOpen: boolean): void => {
      appState = { ...appState, isOpen };
      emitChange();
    },

    setIsEmailAgentOpen: (isEmailAgentOpen: boolean, instanceId?: string): void => {
      appState = { ...appState, isEmailAgentOpen, emailAgentInstanceId: instanceId };
      emitChange();
    },

    setAiOutreachOpen: (isOpen: boolean, domain?: string, companyName?: string): void => {
      appState = {
        ...appState,
        aiOutreach: {
          isOpen,
          domain: domain ?? appState.aiOutreach.domain,
          companyName: companyName ?? appState.aiOutreach.companyName,
        },
      };
      emitChange();
    },

    setToast: (toast: Toast | undefined) => {
      appState = { ...appState, toast };
      emitChange();
    },

    setErrorToast: (toast: Toast) => {
      appState = {
        ...appState,
        toast: {
          icon: 'alert-circle',
          iconColor: colorsPalettes.red[300],
          showCloseIcon: true,
          iconSize: 'sm',
          ...toast,
        },
      };
      emitChange();
    },

    setSuccessToast: (toast: Omit<Toast, 'variant'>) => {
      appState = {
        ...appState,
        toast: {
          variant: 'success',
          ...toast,
        },
      };
      emitChange();
    },

    setCompanyLinkedinId(companyLinkedinId?: string): void {
      if (appState.companyLinkedinId === companyLinkedinId) {
        return;
      }

      appState = { ...appState, companyLinkedinId };
      emitChange();
    },

    setCompanyDomain: (companyDomain: string) => {
      if (appState.companyDomain === companyDomain || isIgnoredDomain(companyDomain)) {
        return;
      }

      appState = { ...appState, companyDomain };
      emitChange();
    },

    setCompanyPage: (params?: { companyDomain?: string; companyLinkedinId?: string }) => {
      const { companyLinkedinId, companyDomain } = params || {};
      if (
        appState.page === Page.Company &&
        (appState.companyLinkedinId === companyLinkedinId || appState.companyDomain === companyDomain)
      ) {
        return;
      }

      appState = {
        ...initialState,
        page: Page.Company,
        companyLinkedinId,
        companyDomain,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        url: appState.url,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        contactFilters: { ...appState.contactFilters, searchText: '' },
        settingsStorage: { ...appState.settingsStorage },
      };
      emitChange();
    },

    setContactPage: (params?: { linkedinId?: string; linkedinSalesNavId?: string }) => {
      const { linkedinId, linkedinSalesNavId } = params || {};
      const isSameLinkedinId = linkedinId && appState.contactLinkedinId === linkedinId;
      const isSameLinkedinSalesNavId = linkedinSalesNavId && appState.contactLinkedinSalesNavId === linkedinSalesNavId;

      if (appState.page === Page.Contact && (isSameLinkedinId || isSameLinkedinSalesNavId)) {
        return;
      }

      appState = {
        ...initialState,
        page: Page.Contact,
        contactLinkedinId: linkedinId,
        contactLinkedinSalesNavId: linkedinSalesNavId,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        url: appState.url,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        contactFilters: { ...appState.contactFilters, searchText: '' },
        settingsStorage: { ...appState.settingsStorage },
      };
      emitChange();
    },

    setSearchPage: () => {
      appState = {
        ...initialState,
        page: Page.Search,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        url: appState.url,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        contactFilters: { ...appState.contactFilters, searchText: '' },
        settingsStorage: { ...appState.settingsStorage },
      };
      emitChange();
    },

    setSettingsPage: () => {
      appState = { ...appState, page: Page.Settings, prevPage: appState.page };
      emitChange();
    },

    resetSettingsPage: () => {
      appState = { ...appState, page: appState.prevPage || Page.Home };
      emitChange();
    },

    setCollectedContactInfo: (collectedContactInfo: Partial<Contact>) => {
      appState = { ...appState, collectedContactInfo };
      emitChange();
    },

    setCollectedContactsSearchInfo: (collectedContactsSearchInfo: Partial<Contact>[]) => {
      appState = { ...appState, collectedContactsSearchInfo };
      emitChange();
    },

    setContactsSearchText(searchText) {
      appState = { ...appState, contactFilters: { ...appState.contactFilters, searchText } };
      emitChange();
    },

    setContactsPersona(persona) {
      appState = { ...appState, contactFilters: { ...appState.contactFilters, persona } };
      emitChange();
    },

    setContactsIncludeCountries(includeCountries) {
      appState = { ...appState, contactFilters: { ...appState.contactFilters, includeCountries } };
      emitChange();
    },

    setContactsDetails(contactDetails) {
      appState = { ...appState, contactFilters: { ...appState.contactFilters, contactDetails } };
      emitChange();
    },

    setContactsStatusFilterValue(value) {
      appState = { ...appState, contactFilters: { ...appState.contactFilters, status: value } };
      emitChange();
    },

    setSelectedContacts: (selectedContacts: Contact[], isMasterCheckboxChecked?: boolean) => {
      appState = { ...appState, selectedContacts, isMasterCheckboxChecked: Boolean(isMasterCheckboxChecked) };
      emitChange();
    },

    addToSelectedContacts: (contact: Contact) => {
      if (appState.selectedContacts.some((c) => c.id === contact.id)) {
        return;
      }
      appState = {
        ...appState,
        selectedContacts: [...appState.selectedContacts, contact],
        isMasterCheckboxChecked: false,
      };
      emitChange();
    },

    removeFromSelectedContacts: (contact: Contact) => {
      if (!appState.selectedContacts.some((c) => c.id === contact.id)) {
        return;
      }
      appState = {
        ...appState,
        selectedContacts: appState.selectedContacts.filter((c) => c.id !== contact.id),
        isMasterCheckboxChecked: false,
      };
      emitChange();
    },

    makeSelectedContactRevealed: (contact: Contact, privateData?: PrivateData) => {
      if (!appState.selectedContacts.some((c) => c.id === contact.id) || !privateData) {
        return;
      }
      appState = {
        ...appState,
        selectedContacts: appState.selectedContacts.map((c) =>
          c.id === contact.id ? { ...contact, isRevealed: true, privateData } : c
        ),
      };
      emitChange();
    },

    setHomePage: () => {
      appState = {
        ...initialState,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        url: appState.url,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        contactFilters: { ...appState.contactFilters, searchText: '' },
        settingsStorage: { ...appState.settingsStorage },
      };
      emitChange();
    },

    setCompanyTabName: (companyTabName) => {
      appState = { ...appState, companyTabName };
      emitChange();
    },

    addNotRevealedContactId: (contactId) => {
      appState = { ...appState, notRevealedContactsIds: [...appState.notRevealedContactsIds, contactId] };
      emitChange();
    },

    getSettingsStorageValue: (key) => appState.settingsStorage[key],

    setSettingsStorageValue: (keyValue) => {
      appState = { ...appState, settingsStorage: { ...appState.settingsStorage, ...keyValue } };
      emitChange();
    },

    setSearchPagePagination: (pagination) => {
      appState = { ...appState, searchPagePagination: pagination };
      emitChange();
    },

    setCalendarPage: () => {
      appState = {
        ...initialState,
        url: appState.url,
        page: Page.Meeting,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        settingsStorage: { ...appState.settingsStorage },
        collectedMeeting: appState.collectedMeeting,
        collectedDomains: appState.collectedDomains,
        companyDomain: appState.collectedMeeting.domains.length > 0 ? appState.companyDomain : undefined,
      };
      emitChange();
    },

    setEmailPage: () => {
      appState = {
        ...initialState,
        url: appState.url,
        page: Page.Email,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        settingsStorage: { ...appState.settingsStorage },
        collectedEmail: appState.collectedEmail,
        companyDomain:
          appState.collectedEmail.recipientsDomains || appState.collectedEmail.senderDomains
            ? appState.companyDomain
            : undefined,
      };
      emitChange();
    },

    setGoogleSheetsPage: () => {
      appState = {
        ...initialState,
        url: appState.url,
        page: Page.GoogleSheets,
        isOpen: appState.isOpen,
        isEmailAgentOpen: appState.isEmailAgentOpen,
        emailAgentInstanceId: appState.emailAgentInstanceId,
        notRevealedContactsIds: appState.notRevealedContactsIds,
        settingsStorage: { ...appState.settingsStorage },
        companyDomain: appState.collectedDomains.length > 0 ? appState.companyDomain : undefined,
      };
      emitChange();
    },

    getCollectedMeeting: () => appState.collectedMeeting,

    setCollectedMeeting: (collectedMeeting) => {
      appState = { ...appState, collectedMeeting, collectedDomains: collectedMeeting.domains };
      emitChange();
    },

    resetCollectedMeeting: () => {
      appState = { ...appState, collectedMeeting: initialState.collectedMeeting, collectedDomains: [] };
      emitChange();
    },

    setCollectedEmail: (collectedEmail) => {
      appState = {
        ...appState,
        collectedEmail,
        collectedDomains: collectedEmail.recipientsDomains || collectedEmail.senderDomains || [],
      };
      emitChange();
    },

    incrementEmailsToGenerateCount: () => {
      appState = {
        ...appState,
        emailGenerationOptions: {
          ...appState.emailGenerationOptions,
          emailsToGenerate: appState.emailGenerationOptions.emailsToGenerate + 1,
        },
      };
      emitChange();
    },

    setCountByInsightId: (countByInsightId) => {
      appState = {
        ...appState,
        emailGenerationOptions: { ...appState.emailGenerationOptions, countByInsightId },
      };
      emitChange();
    },

    setEmailReaction: (requestId, reaction) => {
      const emailReactions = { ...appState.emailGenerationOptions.emailReactions, [requestId]: reaction };
      appState = {
        ...appState,
        emailGenerationOptions: { ...appState.emailGenerationOptions, emailReactions },
      };
      emitChange();
    },

    setRegeneratedOpportunitiesIds: (updatedOpportunitiesIds) => {
      appState = {
        ...appState,
        emailGenerationOptions: {
          ...appState.emailGenerationOptions,
          regeneratedOpportunitiesIds: updatedOpportunitiesIds,
        },
      };
      emitChange();
    },

    setUpdatedOpportunities: (updatedOpportunities) => {
      appState = {
        ...appState,
        emailGenerationOptions: { ...appState.emailGenerationOptions, updatedOpportunities },
      };
      emitChange();
    },

    resetEmailGeneration: () => {
      appState = {
        ...appState,
        emailGenerationOptions: { ...initialState.emailGenerationOptions, sessionId: uuid() },
      };
      emitChange();
    },

    setShowToastAfterSuccessfulGeneration: (showToastAfterSuccessfulGeneration) => {
      appState = {
        ...appState,
        emailGenerationOptions: { ...appState.emailGenerationOptions, showToastAfterSuccessfulGeneration },
      };
      emitChange();
    },

    setIsFirstLoadingAfterOnboarding(isFirstLoadingAfterOnboarding) {
      appState = {
        ...appState,
        emailGenerationOptions: { ...appState.emailGenerationOptions, isFirstLoadingAfterOnboarding },
      };
      emitChange();
    },

    setEmailsToGenerateCount: (count: number) => {
      appState = {
        ...appState,
        emailGenerationOptions: {
          ...appState.emailGenerationOptions,
          emailsToGenerate: count,
        },
      };
      emitChange();
    },

    setCompanyQuotaReached: (isCompanyQuotaReached: boolean) => {
      appState = { ...appState, isCompanyQuotaReached };
      emitChange();
    },

    setCollectedDomains: (domains: string[]) => {
      appState = {
        ...appState,
        collectedDomains: domains,
        companyDomain: domains.length > 0 ? domains[0] : undefined,
      };
      emitChange();
    },

    setHasRequestedAccess: (value) => {
      appState = {
        ...appState,
        hasRequestedAccess: { ...appState.hasRequestedAccess, ...value },
      };
      emitChange();
    },

    setWidgetId: (widgetId) => {
      appState = {
        ...appState,
        page: Page.Company,
        companyTabName: CompanyTabName.Overview,
        widgetId,
      };
      emitChange();
    },
  };
};

export const appStore = makeAppStore();
